import { Field, Float, ID, InputType, ObjectType } from 'type-graphql'
import { modelOptions, Prop, PropType } from '@typegoose/typegoose'
import { BaseEntity } from '../base/BaseEntity'
import { Payment } from '../payment/Payment'
import { Address } from '../address/Address'
import { Communication } from '../communication/Communication'
import { PaymentBalance } from '../payment/PaymentBalance'
import { DocumentLock } from '../documentLock/DocumentLock'
import { RelatedEntity } from '../base/RelatedEntity'
import { Activity } from '../activity/Activity'
import type { PurchaseOrderStatusEnum } from './PurchaseOrderStatusEnum'
import type { PurchaseOrderPaymentStatusEnum } from './PurchaseOrderPaymentStatusEnum'
import { PurchaseOrderItem } from './PurchaseOrderItem'
import { Asset } from '../asset/Asset'
import { Vendor } from '../vendor/Vendor'
import { User } from '../user/User'
import { WorkOrder } from '../work-order/WorkOrder'
import { generateEntityId } from '../generateEntityId'

@ObjectType()
@InputType('PurchaseOrderInput')
@modelOptions({ schemaOptions: { timestamps: true, minimize: true } })
export class PurchaseOrder extends BaseEntity {
  @Field(() => ID)
  @Prop({ type: String, required: true, default: () => generateEntityId('po') })
  declare _id: string

  @Field(() => Float)
  @Prop({ type: Number })
  net: number

  @Field(() => Float)
  @Prop({ type: Number })
  gross: number

  @Field(() => String)
  @Prop({ type: String })
  declare status: PurchaseOrderStatusEnum

  @Field(() => Float)
  @Prop({ type: Number })
  discount: number

  @Field(() => Float)
  @Prop({ type: Number })
  discountPercent: number

  @Field(() => Float)
  @Prop({ type: Number })
  taxAmount: number

  @Field(() => String)
  @Prop({ type: String })
  paymentStatus: PurchaseOrderPaymentStatusEnum

  @Field(() => String)
  @Prop({ type: String })
  paymentTerms: string

  @Field(() => String)
  @Prop({ type: String })
  qboId: string

  @Field(() => Vendor)
  @Prop({ type: Vendor })
  vendor: Vendor

  @Field(() => User)
  @Prop({ type: User })
  shipTo: User

  @Field(() => Address)
  @Prop({ type: Address })
  vendorAddress: Address

  @Field(() => [PurchaseOrderItem])
  @Prop({ type: () => [PurchaseOrderItem] })
  items: PurchaseOrderItem[]

  @Field(() => [Payment])
  @Prop({ type: [Payment] })
  payments: Payment[]

  @Field(() => Address)
  @Prop({ type: Address })
  shipToAddress: Address

  @Field(() => Communication)
  @Prop({ type: Communication })
  communication: Communication

  //This has field resolver, but we put it here for type-safety too
  @Field(() => PaymentBalance)
  paymentBalance: PaymentBalance

  // Channel-neutral conversation handle (field resolver): the linked conversation's externalId.
  // Lets the communication UI mount the thread without re-reading anything else.
  @Field(() => String, { nullable: true })
  activeConversationId?: string

  @Field(() => String)
  @Prop({ type: String })
  poNotes: string

  //This has field resolver, but we put it here for type-safety too
  @Field(() => DocumentLock)
  documentLock: DocumentLock

  /**
   * These are the _ids of purchase orders that are related to this
   */
  @Field(() => [PurchaseOrder])
  @Prop({ type: [RelatedEntity] })
  relatedPurchaseOrders: PurchaseOrder[]

  /**
   * Work orders related to this purchase order (bidirectional link between procurement and production)
   */
  @Field(() => [WorkOrder])
  @Prop({ type: [RelatedEntity] }, PropType.ARRAY)
  relatedWorkOrders: ReturnType<() => WorkOrder[]>

  @Field(() => [Asset])
  @Prop({ type: [Asset] })
  assets: Asset[]

  @Field(() => [Activity])
  @Prop({ type: [Activity] })
  remarksActivity: Activity[]

  @Field(() => Date)
  @Prop({ type: Date })
  estimatedArrivalDate: Date

  @Field(() => String)
  @Prop({ type: String })
  shipVia: string

  @Field(() => Date)
  @Prop({ type: Date, default: Date.now })
  purchaseDate: Date

  @Field(() => [User])
  @Prop({ type: [RelatedEntity] })
  members: User[]
}
